import { SBCRewardType } from "@/api/privateRequests/sbcs/getSbcSets";
import { ImageWithFallback } from "@/components/ImageWithFallback";
import Divider from "@/components/atoms/Divider/Divider";
import { PlatformChoice } from "@/components/organisms/SBCs/PlatformChoice/PlatformChoice";
import { Tier } from "@/redux/players/playersSlice";
import { BREAKPOINTS } from "@/settings";
import { clsx } from "clsx";
import { useMediaQuery } from "usehooks-ts";

import { SBCRequirements } from "../SBCRequirements";
import { SBCReward } from "../SBCReward";

type Props = {
  name: string;
  imageURL: string;
  reward: SBCRewardType;
  rewardUrl: string;
  playerRole: string;
  tier: Tier;
  description?: string;
  requirements: string[];
  formattedPrice: string;
  sbcToggle: React.ReactNode;
  platform: "Playstation" | "PC";
  isOnSolutionsPage: boolean;
};

const SBCDesktop = ({
  name,
  imageURL,
  reward,
  rewardUrl,
  playerRole,
  tier,
  description,
  requirements,
  formattedPrice,
  sbcToggle,
  platform,
  isOnSolutionsPage,
}: Props) => {
  if (isOnSolutionsPage) {
    return (
      <div className="cursor-auto">
        <div className="flex px-4 py-3 items-center">
          <ImageWithFallback
            className="h-14 w-14 -ml-4 -mr-1 -mb-1 "
            src={imageURL}
            alt={imageURL + "image"}
            fallback={
              import.meta.env.VITE_APP_ASSETS_BASE_URL + "/misc/default-sbc.png"
            }
          />
          <div className="w-full">
            <div className="flex justify-between items-center h-5">
              <div className="text-sm">{name}</div>
              <div className="flex items-center">{sbcToggle}</div>
            </div>
            <div className="flex items-center gap-1 h-5">
              <img
                className="w-3 h-3 desktop:w-[12px] desktop:h-[12px]"
                src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc24/misc/fc24coin.png`}
                alt="ut-coin"
              />
              <span className="text-xs text-dark-tertiary">
                {formattedPrice}
              </span>
              <PlatformChoice platform={platform} />
            </div>
          </div>
        </div>
        <div>
          <div className="bg-panel-middleground mx-4 mb-1 p-2 rounded-t-lg">
            <SBCRequirements requirements={requirements} />
          </div>
          <div className="bg-panel-middleground m-4 mt-0 rounded-b-lg">
            <SBCReward
              reward={reward}
              rewardUrl={rewardUrl}
              playerRole={playerRole}
              tier={tier}
              description={description}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "group bg-panel-background rounded-lg mb-4 p-4 pt-2 transition-transform duration-150 hover:scale-[1.004] hover:bg-panel-middleground",
      )}
    >
      <div className="flex justify-between items-center mb-1">
        <div className="flex gap-5">
          <div className="flex items-center gap-x-1">
            <img
              className="w-3 h-3 desktop:w-[17px] desktop:h-[17px]"
              src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc24/misc/fc24coin.png`}
              alt="ut-coin"
            />
            <span className="font-xs text-dark-primary font-medium">
              {formattedPrice}
            </span>
            <PlatformChoice platform={platform} />
          </div>
          <div className="font-semibold">{name}</div>
        </div>
        <div className="flex items-center">{sbcToggle}</div>
      </div>
      <div className="flex gap-5">
        <div className="w-1/2 flex gap-2">
          <div className="w-24">
            <ImageWithFallback
              loading="lazy"
              className="h-24 object-cover"
              src={imageURL}
              alt={name}
              fallback={
                import.meta.env.VITE_APP_ASSETS_BASE_URL +
                "/misc/default-sbc.png"
              }
            />
          </div>
          <div className={clsx("my-2")}>
            <Divider
              direction="vertical"
              color="bg-panel-foreground"
              thickness="w-0.5"
              length="h-full"
            />
          </div>
          <div className={clsx("py-3")}>
            <SBCRequirements requirements={requirements} />
          </div>
        </div>
        <div className="w-1/2 bg-panel-middleground rounded-lg group-hover:bg-panel-foreground">
          <SBCReward
            reward={reward}
            rewardUrl={rewardUrl}
            playerRole={playerRole}
            tier={tier}
            description={description}
          />
        </div>
      </div>
    </div>
  );
};

export default SBCDesktop;
